if (!customElements.get('complete-the-space')) {
  class CompleteTheSpace extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.items = Array.from(this.querySelectorAll('[data-complete-space-item]'));
      this.moneyFormat = this.dataset.moneyFormat;

      if (!this.items.length) return;

      this.onChange = this.handleChange.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);

      this.addEventListener('change', this.onChange);
      this.addEventListener('submit', this.onSubmit);

      this.overlapControls = Array.from(document.querySelectorAll('.product-add-to-cart-sticky, .back-to-top'));
      if (this.overlapControls.length && 'IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          this.overlapControls.forEach((control) => {
            control.classList.toggle('complete-space-overlap-hidden', entries[0].isIntersecting);
          });
        });
        this.visibilityObserver.observe(this);
      }

      this.items.forEach((item) => this.updateItem(item));
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      this.removeEventListener('change', this.onChange);
      this.removeEventListener('submit', this.onSubmit);
      if (this.visibilityObserver) this.visibilityObserver.disconnect();
      if (this.overlapControls) {
        this.overlapControls.forEach((control) => control.classList.remove('complete-space-overlap-hidden'));
      }

      this.initialized = false;
    }

    handleChange(event) {
      const control = event.target instanceof Element
        ? event.target.closest('[data-complete-space-variant]')
        : null;
      if (!control || !this.contains(control)) return;

      const item = control.closest('[data-complete-space-item]');
      if (!item) return;

      this.clearItemMessages(item);
      this.updateItem(item);
    }

    handleSubmit(event) {
      const form = event.target instanceof Element
        ? event.target.closest('[data-complete-space-item-form]')
        : null;
      if (!form || !this.contains(form)) return;

      event.preventDefault();
      const item = form.closest('[data-complete-space-item]');
      if (item) this.addItem(item, form, event.submitter);
    }

    updateItem(item) {
      const rowSelect = item.querySelector('[data-complete-space-variant]');
      const option = rowSelect && rowSelect.selectedOptions ? rowSelect.selectedOptions[0] : null;
      if (!option) return;

      const available = option.dataset.available === 'true' && !option.disabled;
      const price = Number(option.dataset.price || 0);
      const comparePrice = Number(option.dataset.comparePrice || 0);
      const submitting = item.dataset.submitting === 'true';

      item.dataset.variantId = option.value;
      item.dataset.price = String(price);
      item.dataset.available = String(available);

      const variantInput = item.querySelector('[data-complete-space-variant-input]');
      if (variantInput) variantInput.value = option.value;


      item.querySelectorAll('[data-complete-space-variant]')
        .forEach((control) => {
          control.disabled = submitting;
        });

      item.querySelectorAll('[data-complete-space-price]')
        .forEach((element) => this.setMoney(element, price));

      item.querySelectorAll('[data-complete-space-compare]').forEach((element) => {
        if (comparePrice > price) this.setMoney(element, comparePrice);
        element.classList.toggle('complete-space__compare--hidden', comparePrice <= price);
      });

      if (item.dataset.renderedVariantId && item.dataset.renderedVariantId !== option.value) {
        this.swapImage(item.querySelector('.complete-space__item-image'), option.dataset.itemImage);
      }
      item.dataset.renderedVariantId = option.value;
      item.querySelectorAll('[data-complete-space-product-link]').forEach((link) => {
        const url = new URL(link.getAttribute('href'), window.location.origin);
        url.searchParams.set('variant', option.value);
        link.setAttribute('href', url.pathname + url.search + url.hash);
      });

      item.querySelectorAll('[data-complete-space-item-submit]').forEach((submitButton) => {
        const addLabel = submitButton.dataset.addLabel || 'Add to cart';
        const soldOutLabel = submitButton.dataset.soldOutLabel || 'Sold out';
        const productTitle = submitButton.dataset.productTitle || '';
        const visibleLabel = available ? addLabel : soldOutLabel;
        const labelElement = submitButton.querySelector('[data-complete-space-item-submit-label]');

        if (labelElement) labelElement.textContent = visibleLabel;
        submitButton.disabled = !available || submitting;
        submitButton.setAttribute('aria-label', visibleLabel + (productTitle ? ' — ' + productTitle : ''));
      });
    }

    setMoney(element, cents) {
      if (!element) return;

      let bdi = element.querySelector('bdi');
      if (!bdi) {
        bdi = document.createElement('bdi');
        bdi.dir = 'ltr';
        element.replaceChildren(bdi);
      }
      bdi.textContent = this.formatPrice(cents);
    }

    swapImage(image, url) {
      if (!image || !url) return;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = url;
    }

    formatPrice(cents) {
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const format = this.moneyFormat || '{{ amount }}';
      const match = format.match(placeholderRegex);
      if (!match) return (cents / 100).toFixed(2);

      const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
        const value = (Number(number) / 100).toFixed(precision);
        const parts = value.split('.');
        const amount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        return amount + (parts[1] ? decimal + parts[1] : '');
      };

      let value;
      switch (match[1]) {
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'", '.');
          break;
        default:
          value = formatWithDelimiters(cents, 2);
      }

      const formatted = format.replace(placeholderRegex, value);
      const documentFragment = new DOMParser().parseFromString(formatted, 'text/html');
      return documentFragment.body.textContent.trim();
    }

    async addItem(item, form, submitter) {
      if (item.dataset.submitting === 'true') return;

      this.updateItem(item);
      this.clearItemMessages(item);

      const variantId = Number(item.dataset.variantId);
      const statusElement = form.querySelector('[data-complete-space-item-status]');
      const errorElement = form.querySelector('[data-complete-space-item-error]');

      if (!Number.isFinite(variantId) || variantId <= 0 || item.dataset.available !== 'true') {
        if (errorElement) {
          errorElement.textContent = this.dataset.errorMessage;
          errorElement.hidden = false;
        }
        if (submitter) submitter.focus();
        return;
      }

      item.dataset.submitting = 'true';
      this.updateItem(item);
      item.querySelectorAll('[data-complete-space-item-submit]')
        .forEach((button) => button.setAttribute('aria-busy', 'true'));
      if (statusElement) statusElement.textContent = this.dataset.addingMessage || '';

      const formData = new FormData(form);
      formData.set('id', String(variantId));
      formData.set('quantity', '1');
      formData.append('sections', 'cart-drawer,cart-bubble');
      formData.append('sections_url', window.location.pathname);

      const defaultRoot = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      const cartAddUrl = form.action || defaultRoot + 'cart/add.js';

      try {
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json'
          },
          body: formData
        });

        const parsedState = await response.json();
        if (!response.ok || parsedState.status) {
          throw new Error(parsedState.description || parsedState.message || this.dataset.errorMessage);
        }


        const rendered = this.renderCart(parsedState);
        if (!rendered) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }

        const addedProduct = parsedState.items && parsedState.items[0]
          ? parsedState.items[0]
          : parsedState;
        document.documentElement.dispatchEvent(new CustomEvent('cart:item-added', {
          bubbles: true,
          detail: { product: addedProduct }
        }));

        this.clearItemMessages(item);
        const rowStatus = form.querySelector('[data-complete-space-item-status]');
        if (rowStatus) rowStatus.textContent = this.dataset.successMessage;
      } catch (error) {
        if (statusElement) statusElement.textContent = '';
        if (errorElement) {
          errorElement.textContent = error.message || this.dataset.errorMessage;
          errorElement.hidden = false;
        }
        if (submitter) submitter.focus();
      } finally {
        item.dataset.submitting = 'false';
        item.querySelectorAll('[data-complete-space-item-submit]')
          .forEach((button) => button.removeAttribute('aria-busy'));
        this.updateItem(item);
      }
    }

    renderCart(parsedState) {
      const sections = parsedState.sections || {};
      let rendered = false;

      rendered = this.replaceSection('Cart-Drawer', '.cart-drawer', sections['cart-drawer']) || rendered;
      rendered = this.replaceSection('cart-drawer-toggle', '.thb-secondary-area--item-count', sections['cart-bubble']) || rendered;
      rendered = this.replaceSection('cart-drawer-toggle', '.thb-secondary-area--item-amount', sections['cart-bubble']) || rendered;

      const drawer = document.getElementById('Cart-Drawer');
      if (drawer && typeof drawer.open === 'function') {
        if (typeof drawer.removeProductEvent === 'function') drawer.removeProductEvent();
        drawer.open();
      } else if (window.theme && window.theme.settings && window.theme.settings.cart_drawer === false) {
        window.location.assign(window.theme.routes.cart_url);
      }

      return rendered;
    }

    replaceSection(id, selector, html) {
      const root = document.getElementById(id);
      if (!root || !html) return false;

      const incomingDocument = new DOMParser().parseFromString(html, 'text/html');
      const incoming = incomingDocument.querySelector(selector);
      const current = root.querySelector(selector) || (root.matches(selector) ? root : null);
      if (!incoming || !current) return false;

      current.innerHTML = incoming.innerHTML;
      return true;
    }

    clearItemMessages(item) {
      item.querySelectorAll('[data-complete-space-item-status]')
        .forEach((element) => {
          element.textContent = '';
        });
      item.querySelectorAll('[data-complete-space-item-error]')
        .forEach((element) => {
          element.textContent = '';
          element.hidden = true;
        });
    }
  }

  customElements.define('complete-the-space', CompleteTheSpace);
}

if (!customElements.get('complete-space-recommendations')) {
  class CompleteSpaceRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.state === 'ready') {
        this.hidden = false;
        this.removeAttribute('aria-busy');
        this.notifyResolved();
        return;
      }

      if (this.dataset.state === 'empty' || !this.dataset.url) {
        this.collapse();
        return;
      }

      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;

          this.visibilityObserver.disconnect();
          this.visibilityObserver = null;
          this.fetchRecommendations();
        }, { rootMargin: '500px 0px' });
        this.visibilityObserver.observe(this);
      } else {
        this.fetchRecommendations();
      }
    }

    disconnectedCallback() {
      if (this.visibilityObserver) this.visibilityObserver.disconnect();
      if (this.requestController) this.requestController.abort();
      this.visibilityObserver = null;
      this.requestController = null;
      this.requestStarted = false;
    }

    static familyIdentity(title, type = '') {
      const name = String(title || '').normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase();
      const words = name.match(/[\p{L}\p{N}]+/gu) || [];
      const descriptor = words.findIndex((word, index) =>
        /^(sofas?|armchairs?|arm|lounge|boucle|velvet|leather|linen|swivel)$/.test(word)
        || /^(\d+|one|single)$/.test(word) && words[index + 1] === 'seater');
      const family = (descriptor < 0 ? words : words.slice(0, descriptor)).join(' ');
      let kind = /\barm\s*chair\b|\blounge\s+chair\b|\b(?:1|one|single)[ -]*seater\b/.test(name)
        ? 'armchair' : /\bsofa\b/.test(name) ? 'sofa' : '';
      if (!kind) {
        const category = String(type).toLowerCase();
        kind = /arm.*chair|lounge.*chair/.test(category) ? 'armchair' : /sofa/.test(category) ? 'sofa' : '';
      }
      if (!kind || !family || /^(sofa|armchair|arm|chair|lounge|single|one|1)$/.test(family)) return null;
      return { family, kind };
    }

    static matchingItems(items, source, sourceId) {
      return items.filter((item) => {
        const identity = this.familyIdentity(item.dataset.productTitle, item.dataset.productType);
        return item.dataset.productId !== sourceId && item.dataset.available === 'true'
          && identity?.family === source.family && identity.kind !== source.kind;
      });
    }

    static selectItems(matches, fallback, sourceId, limit, sourceCategory = '', allowedCategories = []) {
      const seen = new Set([sourceId]);
      const categories = new Set(sourceCategory ? [sourceCategory] : []);
      const selected = [];
      const add = (item, matching = false) => {
        const id = item.dataset.productId;
        const category = (item.dataset.productCategory || '').trim() || 'other';
        if (!id || seen.has(id) || item.dataset.available !== 'true' || selected.length >= limit) return false;
        if (allowedCategories.length && !allowedCategories.includes(category)) return false;
        if (!matching && categories.has(category)) return false;
        seen.add(id);
        categories.add(category);
        selected.push(item);
        return true;
      };
      for (const match of matches) {
        if (add(match, true)) break;
      }
      fallback.forEach(item => add(item));
      return selected;
    }

    async fetchDocument(url, signal) {
      const response = await fetch(url, { headers: { Accept: 'text/html' }, signal });
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    }

    async fetchFamilyItems(signal) {
      const primaryRoot = `${this.dataset.primaryRootUrl.replace(/\/$/, '')}/`;
      const localRoot = `${this.dataset.rootUrl.replace(/\/$/, '')}/`;
      let title = this.dataset.productTitle;
      let type = this.dataset.productType;

      if (localRoot !== primaryRoot) {
        const response = await fetch(`${primaryRoot}products/${encodeURIComponent(this.dataset.productHandle)}.js`, { signal });
        if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
        const product = await response.json();
        title = product.title;
        type = product.type;
      }
      const source = CompleteSpaceRecommendations.familyIdentity(title, type);
      if (!source) return [];

      const limit = 1;
      const searchUrl = new URL(`${primaryRoot}search`, window.location.origin);
      searchUrl.search = new URLSearchParams({
        q: `title:"${source.family}"`, type: 'product', 'options[unavailable_products]': 'hide',
        'options[prefix]': 'none', section_id: 'complete-space-family'
      });
      const matches = [];
      const seenPages = new Set();
      let nextUrl = searchUrl;
      while (nextUrl && !seenPages.has(nextUrl.href) && matches.length < limit) {
        seenPages.add(nextUrl.href);
        const doc = await this.fetchDocument(nextUrl.href, signal);
        const results = doc.querySelector('template[data-complete-space-family]')?.content;
        if (!results) break;
        const candidates = Array.from(results.querySelectorAll('[data-complete-space-item]'));
        matches.push(...CompleteSpaceRecommendations.matchingItems(candidates, source, this.dataset.productId));
        const next = results.querySelector('[data-complete-space-family-next]');
        nextUrl = next ? new URL(next.getAttribute('href'), searchUrl) : null;
        if (nextUrl) nextUrl.searchParams.set('section_id', 'complete-space-family');
      }
      const selected = CompleteSpaceRecommendations.selectItems(matches, [], this.dataset.productId, limit);
      if (localRoot === primaryRoot) return selected;

      const localized = await Promise.allSettled(selected.map(async (item) => {
        const url = `${localRoot}products/${encodeURIComponent(item.dataset.productHandle)}?section_id=complete-space-family`;
        const doc = await this.fetchDocument(url, signal);
        return doc.querySelector('template[data-complete-space-family]')?.content.querySelector('[data-complete-space-item]');
      }));
      return localized.filter((result) => result.status === 'fulfilled' && result.value).map((result) => result.value);
    }

    async fetchRecommendations() {
      if (this.requestStarted) return;

      this.requestStarted = true;
      this.requestController = new AbortController();
      const { signal } = this.requestController;
      const [related, family] = await Promise.allSettled([
        this.fetchDocument(this.dataset.url, signal),
        this.fetchFamilyItems(signal)
      ]);
      if (signal.aborted) return;

      const incomingSection = related.status === 'fulfilled'
        ? Array.from(related.value.querySelectorAll('[data-complete-space-recommendations]'))
          .find((candidate) => candidate.dataset.sectionId === this.dataset.sectionId)
        : null;
      const root = incomingSection || this.cloneNode(true);
      const matches = family.status === 'fulfilled' ? family.value : [];
      const candidates = Array.from(root.querySelectorAll('.complete-space__items > [data-complete-space-item]'));
      const complements = Array.from(root.querySelector('template[data-complete-space-complements]')?.content.querySelectorAll('[data-complete-space-item]') || []);
      const manual = root.dataset.source === 'theme-manual';
      const items = manual ? candidates.slice(0, Number(this.dataset.productLimit))
        : CompleteSpaceRecommendations.selectItems(matches, [...complements, ...candidates],
          this.dataset.productId, Number(this.dataset.productLimit), this.dataset.productCategory,
          (root.dataset.roomCategories || '').split(',').filter(Boolean));
      const list = root.querySelector('.complete-space__items');
      if (!list || !items.length) {
        this.collapse();
        return;
      }
      list.replaceChildren(...items);
      root.querySelector('template[data-complete-space-complements]')?.remove();
      root.dataset.state = 'ready';
      if (!manual) root.dataset.source = matches.length ? 'family-category-mix' : 'category-mix';
      root.hidden = false;
      root.removeAttribute('aria-busy');
      root.removeAttribute('data-url');
      this.replaceWith(document.importNode(root, true));
    }

    collapse() {
      this.dataset.state = 'empty';
      this.hidden = true;
      this.removeAttribute('aria-busy');
      this.removeAttribute('data-url');
      this.replaceChildren();
      this.notifyResolved();
    }

    notifyResolved() {
      if (this.resolutionNotified) return;

      this.resolutionNotified = true;
      document.dispatchEvent(new CustomEvent('complete-space:resolved', {
        detail: {
          sectionId: this.dataset.sectionId,
          state: this.dataset.state
        }
      }));
    }
  }

  customElements.define('complete-space-recommendations', CompleteSpaceRecommendations);
}
